/*
  Edit Calendar.

  Same two-path layout as Add (see CalendarMethodPicker), except the method and
  provider are fixed: a credential's type is what its stored tokens belong to,
  so the cards render locked and only show which path this credential is on.

  Re-authenticating an automatic credential brings the account's calendars back
  with it, so this hands over to the same CalendarEntriesModal that Add does -
  which is the only way to run import / map against a credential saved earlier.
*/

import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { Typography } from "@mui/material";
import { setCalendar } from "../redux/slices/calendarSlice";
import { toast } from "react-toastify";
import "../components/editmodal.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/EditNote";
import Divider from "@mui/material/Divider";
import CalendarOAuthPanel from "./CalendarOAuthPanel";
import { isOAuthCalendarType } from "../utils/calendarOAuth";
import CalendarMethodPicker, {
  methodForCalendarType,
} from "./CalendarMethodPicker";
import CalendarEntriesModal from "./CalendarEntriesModal";
import { fetchEmmsList } from "../utils/calendarProvisioning";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiTextField-root": {
      margin: theme.spacing(1),
      width: "30ch",
    },
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  paper: {
    position: "absolute",
    width: 400,
    backgroundColor: "#fff",
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.5)",
    padding: 20,
    outline: "none",
    borderRadius: 5,
    overflow: "hidden",
  },
  label: {
    fontWeight: "bold",
  },
  fieldContainer: {
    display: "flex",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1rem",
  },

  buttonSubDiv: {
    display: "flex",
    gap: ".5rem",
  },

  divider: {
    margin: "20px 0",
  },
}));

const EditCalendarForm = ({
  selectedOption,
  calendarData,
  selectedItemId,
  handleClose,
}) => {
  const classes = useStyles();
  const headers = useSelector((state) => state.header);
  const emmsheaders = useSelector((state) => state.emmsheader);
  const oldCalendarData = useSelector((state) => state.calendar);
  const dispatch = useDispatch();

  const [open, setOpen] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    calendarType: "",
    credentialName: "",
    username: "",
    password: "",
    host: "",
    isImpersonateRoom: false,
    customProperties: {},
  });
  // What the latest re-authentication returned, including the calendars on the
  // account. EMMS saves the refreshed credential itself; no token comes back.
  const [oauthResult, setOauthResult] = useState(null);
  const [savedCalendar, setSavedCalendar] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [entriesOpen, setEntriesOpen] = useState(false);

  const isOAuth = isOAuthCalendarType(formData.calendarType);

  // calendarEntries is an object, so only flat keys are editable in a TextField;
  // the full map stays visible in the read-only JSON.
  const editableCustomPropertyKeys = Object.keys(
    formData.customProperties || {}
  ).filter((key) => typeof formData.customProperties[key] !== "object");

  const toggleAdvanced = () => {
    setShowAdvanced((prev) => {
      if (prev) setEditMode(false);
      return !prev;
    });
  };

  const toggleEditMode = () => {
    setEditMode((prev) => !prev);
  };

  const handleCustomPropertyChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      customProperties: {
        ...prevFormData.customProperties,
        [name]: value,
      },
    }));
  };

  const handleAddCustomProperty = () => {
    if (newKey && newValue) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        customProperties: {
          ...prevFormData.customProperties,
          [newKey]: newValue,
        },
      }));
      setNewKey("");
      setNewValue("");
    }
  };

  useEffect(() => {
    const selectedCalendar = calendarData?.find(
      (calendar) => calendar.id === selectedItemId
    );

    if (selectedCalendar) {
      setFormData({
        calendarType: selectedCalendar.calendarType,
        credentialName: selectedCalendar.credentialName,
        // Not sent by the calendar list, so Exchange credentials are re-entered
        // to update.
        username: "",
        password: "",
        host: selectedCalendar.host,
        isImpersonateRoom: selectedCalendar.isImpersonateRoom,
        customProperties: {
          ...selectedCalendar.customProperties,
        },
      });
    }
  }, [calendarData, selectedItemId]);

  const refreshCalendars = () =>
    fetchEmmsList({
      path: "calendar",
      emmsId: selectedOption,
      emmsHeaders: emmsheaders?.emmsheaders?.headers,
      requestConfig: headers,
    })
      .then((calendars) => dispatch(setCalendar(calendars)))
      .catch(() => {});

  // EMMS has already saved the refreshed credential; only its id and the
  // account's calendars come back.
  const handleSignedIn = (outcome) => {
    setOauthResult(outcome || null);
    setSavedCalendar({
      id: outcome?.calendarCredentialId || selectedItemId,
      credentialName: outcome?.credentialName || formData.credentialName,
    });
    refreshCalendars();

    if (outcome?.calendars?.length) {
      setShowAdvanced(false);
      setEditMode(false);
      setOpen(false);
      setEntriesOpen(true);
    }
  };

  const saveCredential = async () => {
    // Google / Office: only customProperties can change without signing in again.
    const body = isOAuth
      ? {
          id: selectedItemId,
          calendarType: formData.calendarType,
          credentialName: formData.credentialName,
          customProperties: formData.customProperties,
        }
      : { ...formData, id: selectedItemId };
    const requestBody = {
      path: "calendar",
      query: {},
      emmsId: `${selectedOption}`,
      body,
      requestType: "POST",
      headers: emmsheaders?.emmsheaders?.headers,
    };

    const api = localStorage.getItem("api");
    const response = await axios.post(
      api + `/emms/piggyback`,
      requestBody,
      headers
    );
    const editedCalendar = Array.isArray(response.data)
      ? response.data[0]
      : response.data;

    const index = oldCalendarData.calendar.findIndex(
      (calendar) => calendar.id === editedCalendar?.id
    );
    const updatedCalendarData = [...oldCalendarData.calendar];
    if (index !== -1) {
      updatedCalendarData[index] = editedCalendar;
    } else {
      updatedCalendarData.push(editedCalendar);
    }
    dispatch(setCalendar(updatedCalendarData));
    return editedCalendar;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!isOAuth && (!formData.username || !formData.password)) {
      toast.error("Enter the username and password to update", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }

    setIsSubmitting(true);

    saveCredential()
      .then(() => {
        setIsSubmitting(false);
        toast.success("Updated successfully", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        handleClose();
      })
      .catch((error) => {
        setIsSubmitting(false);
        toast.error("Couldn't be updated", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      });
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: checked }));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleModalClose = () => {
    setOpen(false);
    setIsSubmitting(false);
    handleClose();
  };

  // The refreshed credential is already saved by the time the entries open.
  const handleEntriesClose = () => {
    setEntriesOpen(false);
    handleModalClose();
  };

  const getLabelWithAsterisk = (labelText, isRequired) => {
    return (
      <div style={{ display: "flex", alignItems: "center", width: "140px" }}>
        <label className={classes.label} htmlFor={labelText}>
          {labelText}
        </label>
        {isRequired && <span style={{ color: "red" }}>*</span>}
      </div>
    );
  };

  return (
    <div>
      <Modal
        open={open}
        onClose={handleModalClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="centered-box" sx={{ maxWidth: 760, width: "95%" }}>
          <form
            className={classes.root}
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
          >
              <div>
                <h3>Edit credentials for {formData.credentialName}</h3>

                {!showAdvanced && (
                  <div className="first-modal">
                    <CalendarMethodPicker
                      method={methodForCalendarType(formData.calendarType)}
                      calendarType={formData.calendarType}
                      onMethodChange={() => {}}
                      onCalendarTypeChange={() => {}}
                      locked
                    />

                    <div className={classes.fieldContainer}>
                      {getLabelWithAsterisk("Credential Name:", true)}
                      <TextField
                        required
                        id="credentialName"
                        name="credentialName"
                        size="small"
                        label="Credential Name"
                        variant="outlined"
                        disabled
                        value={formData.credentialName}
                        onChange={handleChange}
                      />
                    </div>

                    {isOAuth ? (
                      <>
                        <CalendarOAuthPanel
                          calendarType={formData.calendarType}
                          emmsId={selectedOption}
                          emmsHeaders={emmsheaders?.emmsheaders?.headers}
                          requestConfig={headers}
                          calendarId={selectedItemId}
                          signedIn={Boolean(savedCalendar)}
                          reauthenticate
                          onSignedIn={handleSignedIn}
                        />

                        {oauthResult?.calendars?.length ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary" }}
                            >
                              <strong>{oauthResult.calendars.length}</strong>{" "}
                              calendar{" "}
                              {oauthResult.calendars.length === 1
                                ? "entry"
                                : "entries"}{" "}
                              fetched.
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: "none" }}
                              onClick={() => {
                                setOpen(false);
                                setEntriesOpen(true);
                              }}
                            >
                              View entries
                            </Button>
                          </Box>
                        ) : null}
                      </>
                    ) : (
                      <div>
                        <div className={classes.fieldContainer}>
                          {getLabelWithAsterisk("Username:", true)}
                          <TextField
                            required
                            id="username"
                            name="username"
                            size="small"
                            label="eg John Doe"
                            variant="outlined"
                            value={formData.username}
                            onChange={handleChange}
                          />
                        </div>

                        <div className={classes.fieldContainer}>
                          {getLabelWithAsterisk("Password:", true)}
                          <TextField
                            required
                            id="password"
                            name="password"
                            size="small"
                            label="eg john@123"
                            variant="outlined"
                            value={formData.password}
                            onChange={handleChange}
                          />
                        </div>
                        <div className={classes.fieldContainer}>
                          {getLabelWithAsterisk("Host:", true)}
                          <TextField
                            required
                            id="host"
                            name="host"
                            label="https://X.X.X.X"
                            size="small"
                            variant="outlined"
                            value={formData.host}
                            onChange={handleChange}
                          />
                        </div>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(formData.isImpersonateRoom)}
                              onChange={handleCheckboxChange}
                              name="isImpersonateRoom"
                              color="primary"
                            />
                          }
                          label="Impersonate Meeting Room"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className={classes.buttonContainer}>
                <Button
                  style={{ textTransform: "none" }}
                  variant="outlined"
                  color="primary"
                  onClick={toggleAdvanced}
                  startIcon={showAdvanced ? <ArrowBackIcon /> : null}
                >
                  {showAdvanced ? "Back" : "Advanced"}
                </Button>
                {showAdvanced && (
                  <Button
                    style={{ textTransform: "none" }}
                    variant="contained"
                    color="primary"
                    onClick={toggleEditMode}
                    endIcon={!editMode ? <EditIcon /> : null}
                  >
                    {editMode ? "Save Changes" : "Edit JSON"}
                  </Button>
                )}
                {!showAdvanced && (
                  <div className={classes.buttonSubDiv}>
                    <Button
                      style={{ textTransform: "none" }}
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={isSubmitting}
                    >
                      Update
                    </Button>
                    <Button
                      style={{ textTransform: "none" }}
                      variant="outlined"
                      color="error"
                      onClick={handleClose}
                      size="small"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
              {showAdvanced && editMode && (
                <div
                  style={{
                    maxWidth: "500px",
                    marginTop: "10px",
                    padding: "10px",
                    border: "1px solid #ccc",
                  }}
                >
                  <h6>Edit Custom Properties</h6>
                  <div style={{ marginBottom: "10px" }}>
                    {editableCustomPropertyKeys.map((key) => (
                      <div key={key} style={{ margin: "15px 0" }}>
                        <TextField
                          id={key}
                          name={key}
                          label={key}
                          variant="outlined"
                          size="small"
                          value={formData.customProperties[key]}
                          onChange={handleCustomPropertyChange}
                        />
                      </div>
                    ))}
                  </div>
                  {editableCustomPropertyKeys.length > 0 && (
                    <div className={classes.divider}>
                      <Divider />
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <TextField
                      id="newKey"
                      name="newKey"
                      label="New Key"
                      variant="outlined"
                      size="small"
                      value={newKey}
                      onChange={(e) => setNewKey(e.target.value)}
                      style={{ marginRight: "10px" }}
                    />
                    <TextField
                      id="newValue"
                      name="newValue"
                      label="New Value"
                      variant="outlined"
                      size="small"
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      style={{ marginRight: "10px" }}
                    />
                    <Button
                      style={{ textTransform: "none" }}
                      variant="contained"
                      color="primary"
                      onClick={handleAddCustomProperty}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              )}
              {showAdvanced && (
                <div
                  style={{
                    maxWidth: "500px",
                    marginTop: "10px",
                    padding: "10px",
                    border: "1px solid #ccc",
                  }}
                >
                  <h6>Custom Properties</h6>
                  <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                    {JSON.stringify(formData.customProperties, null, 2)}
                  </pre>
                </div>
              )}
          </form>
        </Box>
      </Modal>

      <CalendarEntriesModal
        open={entriesOpen}
        onClose={handleEntriesClose}
        calendarCredentialId={savedCalendar?.id}
        credentialName={savedCalendar?.credentialName || formData.credentialName}
        calendars={oauthResult?.calendars}
        emmsId={selectedOption}
        emmsHeaders={emmsheaders?.emmsheaders?.headers}
        requestConfig={headers}
      />
    </div>
  );
};

export default EditCalendarForm;
